import React, { Component } from "react";
import * as Business from "../../utils/business";
import Loading from "../../components/Loading";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import { MenuItem, DropdownButton } from "react-bootstrap";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Treemap,
} from "recharts";

const C_YELLOW = "#FFE600";
const C_NAVY = "#2E2E38";
const C_GRAY = "#74747A";
const C_LIGHT = "#C4C4CD";
const C_PURPLE = "#6B6B9E";

const TIER1_COLORS = [C_YELLOW, C_NAVY, C_PURPLE, C_LIGHT];
const TIER2_COLORS = [C_YELLOW, C_NAVY, C_GRAY];
const RWA_COLORS = [C_YELLOW, C_NAVY, C_GRAY];

const DASHBOARD_DATA = {
  Consolidated: {
    tier1Ratio: 9.64,
    tier2Ratio: 1.45,
    carRatio: 12.51,
    totalRWA: 368672,
    tier1Capital: [
      { name: "Undistributed profits", value: 74.24 },
      { name: "Capital for investment", value: 19.8 },
      { name: "Additional Tier 1 capital", value: 3.49 },
      { name: "Charter capital", value: 1.6 },
    ],
    tier2Capital: [
      { name: "Subordinated debts", value: 56.51 },
      { name: "80% of general provisions", value: 36.28 },
      { name: "Other funds", value: 7.2 },
    ],
    rwa: [
      { name: "Credit risk", size: 348428, pct: "94.51%" },
      { name: "Market risk", size: 17807, pct: "4.83%" },
      { name: "Operational Risk", size: 2437, pct: "0.66%" },
    ],
  },
  Separated: {
    tier1Ratio: 8.92,
    tier2Ratio: 1.38,
    carRatio: 11.64,
    totalRWA: 352480,
    tier1Capital: [
      { name: "Undistributed profits", value: 71.12 },
      { name: "Capital for investment", value: 21.45 },
      { name: "Additional Tier 1 capital", value: 4.21 },
      { name: "Charter capital", value: 2.1 },
    ],
    tier2Capital: [
      { name: "Subordinated debts", value: 54.2 },
      { name: "80% of general provisions", value: 38.5 },
      { name: "Other funds", value: 7.3 },
    ],
    rwa: [
      { name: "Credit risk", size: 332840, pct: "94.43%" },
      { name: "Market risk", size: 15230, pct: "4.32%" },
      { name: "Operational Risk", size: 4410, pct: "1.25%" },
    ],
  },
};

const renderPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}) => {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight="bold"
    >
      {`${Number(value).toFixed(2)}%`}
    </text>
  );
};

class RwaTreemapContent extends Component {
  render() {
    const { x, y, width, height, index, name, pct, size } = this.props;
    const fill = RWA_COLORS[index % RWA_COLORS.length];
    const textColor = index === 0 ? C_NAVY : "#fff";
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          stroke="#fff"
          strokeWidth={2}
          style={{ cursor: "pointer" }}
        />
        {width > 60 && height > 40 && (
          <text
            x={x + width / 2}
            y={y + height / 2 - (height > 80 ? 14 : 0)}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.min(14, Math.max(9, width / 8))}
            fontWeight="bold"
          >
            {name}
          </text>
        )}
        {width > 60 && height > 70 && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 8}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.min(13, Math.max(9, width / 9))}
          >
            {pct}
          </text>
        )}
        {width > 80 && height > 95 && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 26}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.min(11, Math.max(9, width / 10))}
          >
            {(size || 0).toLocaleString()}
          </text>
        )}
      </g>
    );
  }
}

const PieCustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        padding: "8px 12px",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: "bold", color: C_NAVY, marginBottom: 2 }}>
        {item.name}
      </div>
      <div style={{ color: C_GRAY }}>{Number(item.value).toFixed(2)}%</div>
    </div>
  );
};

const RwaTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        padding: "8px 12px",
        borderRadius: 4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: "bold", color: C_NAVY, marginBottom: 2 }}>
        {item.name}
      </div>
      <div style={{ color: C_GRAY }}>{item.pct}</div>
      <div style={{ color: C_GRAY }}>
        {(item.size || 0).toLocaleString()} VND Billion
      </div>
    </div>
  );
};

const cardStyle = (highlight) => ({
  flex: 1,
  background: highlight ? C_NAVY : "#fff",
  borderRadius: 6,
  padding: "18px 20px",
  textAlign: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
  borderBottom: `3px solid ${highlight ? C_YELLOW : "#e8e8e8"}`,
});

const panelStyle = {
  flex: 1,
  background: "#fff",
  borderRadius: 6,
  padding: "16px 16px 12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

const panelTitleStyle = {
  fontWeight: "600",
  color: C_NAVY,
  fontSize: 13,
  marginBottom: 8,
  paddingLeft: 8,
  borderLeft: `3px solid ${C_YELLOW}`,
};

export default class Car extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      date: moment().local().endOf("day"),
      isShowDashboard: false,
      reportType: "Consolidated",
    };
  }

  componentDidMount() {
    this.handleEffectShowDashboard();
  }

  handleEffectShowDashboard() {
    this.setState({ loading: true }, () => {
      setTimeout(
        () => this.setState({ loading: false, isShowDashboard: true }),
        500,
      );
    });
  }

  getData() {
    return DASHBOARD_DATA[this.state.reportType] || DASHBOARD_DATA.Consolidated;
  }

  renderDashboard() {
    if (this.state.loading) {
      return (
        <div style={{ height: Business.getHeightContent() }}>
          <Loading size="small" />
        </div>
      );
    }
    if (!this.state.isShowDashboard) {
      return <div style={{ minHeight: 500 }} />;
    }

    const d = this.getData();

    const kpiCards = [
      {
        label: "Tier 1 Capital Ratio",
        value: `${d.tier1Ratio}%`,
        highlight: false,
      },
      {
        label: "+ Tier 2 Capital Ratio",
        value: `${d.tier2Ratio}%`,
        highlight: false,
      },
      {
        label: "= Capital Adequacy Ratio (CAR)",
        value: `${d.carRatio}%`,
        highlight: true,
      },
      {
        label: "Total Risk Weighted Assets",
        value: d.totalRWA.toLocaleString(),
        sub: "VND Billion",
        highlight: false,
      },
    ];

    return (
      <div style={{ marginTop: 14 }}>
        {/* KPI Cards */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          {kpiCards.map((k, i) => (
            <div key={i} style={cardStyle(k.highlight)}>
              <div
                style={{
                  fontSize: 12,
                  color: k.highlight ? "rgba(255,255,255,0.7)" : C_GRAY,
                  marginBottom: 8,
                  lineHeight: 1.4,
                  minHeight: 32,
                }}
              >
                {k.label}
              </div>
              <div
                style={{
                  fontSize: 30,
                  fontWeight: "bold",
                  color: k.highlight ? C_YELLOW : C_NAVY,
                }}
              >
                {k.value}
              </div>
              {k.sub && (
                <div
                  style={{
                    fontSize: 11,
                    color: k.highlight ? "rgba(255,255,255,0.55)" : C_LIGHT,
                    marginTop: 4,
                  }}
                >
                  {k.sub}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div style={{ display: "flex", gap: 12 }}>
          {/* Tier 1 Capital Donut */}
          <div style={panelStyle}>
            <div style={panelTitleStyle}>Contribution to Tier 1 Capital</div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={d.tier1Capital}
                  cx="50%"
                  cy="48%"
                  innerRadius={58}
                  outerRadius={98}
                  dataKey="value"
                  labelLine={false}
                  label={renderPieLabel}
                  isAnimationActive={true}
                >
                  {d.tier1Capital.map((e, i) => (
                    <Cell
                      key={i}
                      fill={TIER1_COLORS[i % TIER1_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieCustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => (
                    <span style={{ fontSize: 11, color: C_NAVY }}>{v}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Tier 2 Capital Donut */}
          <div style={panelStyle}>
            <div style={panelTitleStyle}>Contribution to Tier 2 Capital</div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={d.tier2Capital}
                  cx="50%"
                  cy="48%"
                  innerRadius={58}
                  outerRadius={98}
                  dataKey="value"
                  labelLine={false}
                  label={renderPieLabel}
                  isAnimationActive={true}
                >
                  {d.tier2Capital.map((e, i) => (
                    <Cell
                      key={i}
                      fill={TIER2_COLORS[i % TIER2_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieCustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => (
                    <span style={{ fontSize: 11, color: C_NAVY }}>{v}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* RWA Treemap */}
          <div style={panelStyle}>
            <div style={panelTitleStyle}>
              Contribution to Risk-Weighted Assets (RWA)
            </div>
            <ResponsiveContainer width="100%" height={258}>
              <Treemap
                data={d.rwa}
                dataKey="size"
                stroke="#fff"
                content={<RwaTreemapContent />}
              >
                <Tooltip content={<RwaTooltip />} />
              </Treemap>
            </ResponsiveContainer>
            <div
              style={{
                display: "flex",
                gap: 14,
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              {d.rwa.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 11,
                    color: C_NAVY,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: RWA_COLORS[i],
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  {item.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div
        className="card"
        style={{ border: "none", backgroundColor: "#F2F2F2" }}
      >
        <div className="header" />
        <div className="content table-responsive table-full-width">
          <div className="row">
            <div className="col-md-3">
              <div style={{ marginRight: 10, display: "inline" }}>
                <label>Date</label>
              </div>
              <SingleDatePicker
                date={this.state.date}
                onDateChange={(date) => this.setState({ date })}
                focused={this.state.focused}
                onFocusChange={({ focused }) => this.setState({ focused })}
                showDefaultInputIcon
                isOutsideRange={() => false}
                inputIconPosition="after"
              />
            </div>
            <div className="col-md-6" />
            <div className="col-md-3">
              <div style={{ marginRight: 10, display: "inline" }}>
                <label>Report type</label>
              </div>
              <DropdownButton
                title={this.state.reportType}
                id="dropdown-car-report-type"
              >
                <MenuItem
                  eventKey="Consolidated"
                  onSelect={(k) =>
                    this.setState({ reportType: k }, () =>
                      this.handleEffectShowDashboard(),
                    )
                  }
                >
                  Consolidated
                </MenuItem>
                <MenuItem divider />
                <MenuItem
                  eventKey="Separated"
                  onSelect={(k) =>
                    this.setState({ reportType: k }, () =>
                      this.handleEffectShowDashboard(),
                    )
                  }
                >
                  Separated
                </MenuItem>
              </DropdownButton>
            </div>
          </div>
          {this.renderDashboard()}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// src/pages/Dashboard/Car.js